<script setup lang="ts">
import { ref, useAttrs, useId } from 'vue'
import { chipParts, useChipRow } from './chip-row/useChipRow'

defineOptions({ inheritAttrs: false })

// Labels are supplied by each surface's TR/EN catalogue, as with shared auth UI.
const props = defineProps<{ moreLabel: string; menuLabel: string }>()
const attrs = useAttrs()
const moreText = () => String(attrs['data-more-label'] ?? props.moreLabel)
const groupName = () => String(attrs['aria-label'] ?? props.menuLabel)
const focusedItem = ref<number>()
const row = ref<HTMLElement>()
const more = ref<HTMLButtonElement>()
const menu = ref<HTMLElement>()
const menuId = useId()
const { hidden, open, menuTarget, show, close, keydown, activate } = useChipRow(row, more, menu)
function openKey(event: KeyboardEvent) {
  if (!open.value && ['ArrowDown', 'Enter', ' '].includes(event.key)) { event.preventDefault(); void show() }
}
</script>

<template>
  <div
    ref="row"
    role="group"
    v-bind="$attrs"
    :aria-label="groupName()"
    :data-more-label="moreText()"
    :data-escape-layer="open ? '' : undefined"
    class="fw-chip-row"
    data-bee-component="chip-overflow"
    @keydown.esc="open && keydown($event)"
  >
    <slot />
    <button
      ref="more"
      type="button"
      class="fw-chip-more"
      hidden
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-controls="open ? menuId : undefined"
      :aria-label="`${moreText()} · ${groupName()} (${hidden.length})`"
      @click="open ? close(true) : show()"
      @keydown="openKey"
    >
      <span>{{ moreText() }}</span><svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.2"
        aria-hidden="true"
      ><path d="M6 9l6 6 6-6" /></svg>
    </button>
    <Teleport :to="menuTarget">
      <div
        v-if="open"
        :id="menuId"
        ref="menu"
        class="fw-chip-menu"
        role="menu"
        :aria-label="`${moreText()} · ${groupName()}`"
        data-bee-component="chip-overflow-menu"
        @keydown="keydown"
      >
        <button
          v-for="(chip, index) in hidden"
          :key="index"
          type="button"
          role="menuitemradio"
          :aria-checked="chip.getAttribute('aria-pressed') === 'true' || chip.getAttribute('aria-selected') === 'true'"
          :class="{ 'fw-chip-menu-focus': focusedItem === index }"
          :aria-disabled="chip.getAttribute('aria-disabled') === 'true' || chip.hasAttribute('disabled') ? 'true' : undefined"
          :title="chip.title || undefined"
          @focus="focusedItem = index"
          @blur="focusedItem = undefined"
          @mouseleave="focusedItem === index && (focusedItem = undefined)"
          @click="activate(chip)"
        >
          <span class="fw-chip-menu-label">{{ chipParts(chip).label }}</span><span class="fw-chip-menu-count">{{ chipParts(chip).count }}</span>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<style src="./chip-row.css"></style>
